import * as styles from "./ChannelList.css";

import type { JSX } from "react";

import { Button } from "@frontend/components/atoms/Button";
import { Icon } from "@frontend/components/atoms/Icon";
import { ContactAddressPlaceLabels } from "@frontend/features/addressbook";
import { useArrayField } from "@frontend/hooks/form";
import type { ArrayItemField } from "@frontend/lib/form";
import {
  CityField,
  CountryField,
  ExtendedAddressField,
  PostalCodeField,
  StateField,
  StreetField,
  useAddressAutofill,
} from "@frontend/shared/address-fields";
import { useLingui } from "@lingui/react/macro";

import { clientForm } from "../stores/editor";
import { PlaceSelect } from "./ChannelList";

const fields = clientForm.fields;

type AddressItem = ArrayItemField<typeof fields.addresses>;

export function AddressList(): JSX.Element {
  const { t } = useLingui();

  const rows = useArrayField(fields.addresses);

  return (
    <div className={styles.list}>
      {rows.isEmpty ? <p className={styles.empty}>{t`No address.`}</p> : null}

      {rows.items.map((item, index) => (
        <div key={item.path} className={styles.addressRow}>
          <Button
            variant="ghost"
            size="sm"
            className={styles.remove}
            aria-label={t`Remove`}
            onClick={() => rows.remove(rows.itemAtoms[index]!)}
          >
            <Icon name="CircleMinus" size="sm" />
          </Button>

          <AddressFields item={item} />
        </div>
      ))}

      <Button
        variant="link"
        size="sm"
        leftSection={<Icon name="Plus" size="sm" />}
        onClick={() =>
          rows.append({
            typedAddressId: null,
            place: "HOME",
            street: null,
            extendedAddress: null,
            postOfficeBox: null,
            postalCode: null,
            city: null,
            state: null,
            country: null,
          })
        }
      >
        {t`Add an address`}
      </Button>
    </div>
  );
}

function AddressFields({ item }: { item: AddressItem }): JSX.Element {
  const fields = item.fields;

  const autofill = useAddressAutofill(fields);

  return (
    <div className={styles.addressFields}>
      <div className={styles.wide}>
        <PlaceSelect field={fields.place} labels={ContactAddressPlaceLabels} />
      </div>

      <div className={styles.wide}>
        <StreetField
          field={fields.street}
          countryField={fields.country}
          size="sm"
          onAddressSelected={autofill.onAddressSelected}
        />
      </div>

      <div className={styles.wide}>
        <ExtendedAddressField field={fields.extendedAddress} size="sm" />
      </div>

      <PostalCodeField field={fields.postalCode} size="sm" />

      <CityField
        field={fields.city}
        countryField={fields.country}
        size="sm"
        onCitySelected={autofill.onCitySelected}
        onCityCleared={autofill.onCityCleared}
      />

      <StateField
        field={fields.state}
        countryField={fields.country}
        size="sm"
      />

      <CountryField field={fields.country} size="sm" />
    </div>
  );
}
